// The volunteer's pre-screen page, /s/:token (Contact Hub 3, Task 12; spec §4.3).
//
// Every action is a POST to screeningForm with the token in the body, never in a query string (R14). The first
// screen shows the payment line, the privacy link and the consent tick before any question; nothing is sent to the
// server until Start (R27), and answers live only in React state: never in localStorage or sessionStorage (a shared
// phone). After Start the page autosaves 1.5 s after the last change. The page never shows a name, panel number or
// study number (Review Focus 2): it shows only what the endpoint serves.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PrivacyNoticeLink from './PrivacyNoticeLink';
import ScreeningDone, { SCREENING_PRIVACY_LABEL, SCREENING_PRIVACY_PATH } from './ScreeningDone';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:7071';
const ENDPOINT = `${API_BASE}/api/screeningForm`;
const AUTOSAVE_DELAY_MS = 1500;
/** The server's limit on a text answer (TEXT_ANSWER_MAX in api/src/functions/preScreenLogic.ts). */
const TEXT_MAX = 1000;

const EXPIRED_TEXT =
  "Sorry, this link has expired. Just reply to our message or email and we'll happily send you a new one.";
const CLOSED_TEXT =
  'Sorry, this study is no longer taking volunteers. Thank you so much for your interest, and please do look out for our future studies.';
const NOT_FOUND_TEXT = "Sorry, we couldn't find these questions. Please use the link in your latest message from us.";
const ERROR_TEXT = 'Sorry, something went wrong. Please try again a little later.';
const ANSWERS_ERROR_TEXT = "Sorry, we couldn't accept some of your answers. Please check each question and try again.";
const RATE_LIMITED_TEXT = 'Sorry, that was a few too many tries. Please wait a minute and try again.';

type AskType = 'yesno' | 'number' | 'choice' | 'text';
interface ServedQuestion {
  id: string;
  text: string;
  type: AskType;
  options?: { id: string; label: string }[];
  multi?: boolean;
  inputMin?: number;
  inputMax?: number;
  optional?: boolean;
}
type Answer = boolean | number | string | string[];
/** What the page holds while the volunteer types: a number stays as its typed text until it is sent. */
type DraftValue = boolean | string | string[];
type Draft = Record<string, DraftValue>;

interface FormData {
  version: number;
  title: string;
  intro: string;
  questions: ServedQuestion[];
  expiresOn: string;
  consentText: string;
  paymentWarning: string;
}

type View =
  | { kind: 'loading' }
  | { kind: 'consent' }
  | { kind: 'questions' }
  | { kind: 'submitted' }
  | { kind: 'expired' }
  | { kind: 'closed' }
  | { kind: 'not-found' }
  | { kind: 'error' };

type SaveNote = 'none' | 'saving' | 'saved' | 'not-saved';

/** The answer the server would accept for one question, or undefined when there is none yet. */
function answerFor(q: ServedQuestion, v: DraftValue | undefined): Answer | undefined {
  switch (q.type) {
    case 'yesno':
      return typeof v === 'boolean' ? v : undefined;
    case 'number': {
      if (typeof v !== 'string' || v.trim() === '') return undefined;
      const n = Number(v);
      if (!Number.isFinite(n)) return undefined;
      if (q.inputMin !== undefined && n < q.inputMin) return undefined;
      if (q.inputMax !== undefined && n > q.inputMax) return undefined;
      return n;
    }
    case 'choice': {
      const ids = new Set((q.options ?? []).map(o => o.id));
      if (q.multi) {
        if (!Array.isArray(v)) return undefined;
        const picked = v.filter(id => ids.has(id));
        return picked.length > 0 ? picked : undefined;
      }
      return typeof v === 'string' && ids.has(v) ? v : undefined;
    }
    case 'text': {
      if (typeof v !== 'string') return undefined;
      const t = v.trim();
      return t.length >= 1 && t.length <= TEXT_MAX ? t : undefined;
    }
    default:
      return undefined;
  }
}

/** Only answers the server would accept, so an autosave of a half-typed number is never refused. */
function answersFrom(questions: ServedQuestion[], draft: Draft): Record<string, Answer> {
  const out: Record<string, Answer> = {};
  for (const q of questions) {
    const a = answerFor(q, draft[q.id]);
    if (a !== undefined) out[q.id] = a;
  }
  return out;
}

function draftFrom(questions: ServedQuestion[], saved: unknown): Draft {
  const out: Draft = {};
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return out;
  const values = saved as Record<string, unknown>;
  for (const q of questions) {
    if (!Object.prototype.hasOwnProperty.call(values, q.id)) continue;
    const v = values[q.id];
    if (typeof v === 'number') out[q.id] = String(v);
    else if (typeof v === 'boolean' || typeof v === 'string') out[q.id] = v;
    else if (Array.isArray(v)) out[q.id] = v.filter((x): x is string => typeof x === 'string');
  }
  return out;
}

function parseQuestions(raw: unknown): ServedQuestion[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (q): q is ServedQuestion =>
      !!q && typeof q === 'object' && typeof (q as ServedQuestion).id === 'string' &&
      typeof (q as ServedQuestion).text === 'string' &&
      ['yesno', 'number', 'choice', 'text'].includes((q as ServedQuestion).type)
  );
}

/** A refusal that ends the page (404 / 410), or null. */
function endViewFor(status: number, error: unknown): View | null {
  if (status === 404) return { kind: 'not-found' };
  if (status === 410) return error === 'expired' ? { kind: 'expired' } : { kind: 'closed' };
  return null;
}

async function post(body: Record<string, unknown>): Promise<{ status: number; data: Record<string, unknown> | null }> {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  return { status: response.status, data };
}

function Message({ text }: { text: string }) {
  return (
    <div className="form-container">
      <div className="form-body">
        <p>{text}</p>
      </div>
    </div>
  );
}

function ScreeningPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [form, setForm] = useState<FormData | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [consent, setConsent] = useState(false);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveNote, setSaveNote] = useState<SaveNote>('none');

  const mounted = useRef(true);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const draftRef = useRef<Draft>({});
  const formRef = useRef<FormData | null>(null);
  const ended = useRef(false);

  const clearSaveTimer = () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
  };

  const end = useCallback((next: View) => {
    ended.current = true;
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    setView(next);
  }, []);

  const open = useCallback(async () => {
    setError(null);
    setView({ kind: 'loading' });
    try {
      const { status, data } = await post({ action: 'open', token });
      if (!mounted.current) return;
      const endView = endViewFor(status, data?.error);
      if (endView) return end(endView);
      if (status !== 200 || !data?.ok) return end({ kind: 'error' });
      if (data.state === 'submitted') return end({ kind: 'submitted' });
      if (data.state !== 'open' || typeof data.version !== 'number') return end({ kind: 'error' });
      const questions = parseQuestions(data.questions);
      const next: FormData = {
        version: data.version,
        title: typeof data.title === 'string' ? data.title : '',
        intro: typeof data.intro === 'string' ? data.intro : '',
        questions,
        expiresOn: typeof data.expiresOn === 'string' ? data.expiresOn : '',
        consentText: typeof data.consentText === 'string' ? data.consentText : '',
        paymentWarning: typeof data.paymentWarning === 'string' ? data.paymentWarning : '',
      };
      const started = data.started === true;
      const restored = started ? draftFrom(questions, data.answers) : {};
      ended.current = false;
      formRef.current = next;
      draftRef.current = restored;
      setForm(next);
      setDraft(restored);
      setConsent(false);
      setIndex(0);
      setSaveNote('none');
      setView({ kind: started ? 'questions' : 'consent' });
    } catch {
      if (mounted.current) end({ kind: 'error' });
    }
  }, [token, end]);

  useEffect(() => {
    mounted.current = true;
    ended.current = false;
    if (!token) {
      end({ kind: 'not-found' });
    } else {
      void open();
    }
    return () => {
      mounted.current = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [token, open, end]);

  /** One save at a time, in order; it sends the answers as they are when it runs. */
  const queueSave = useCallback(() => {
    saveChain.current = saveChain.current.then(async () => {
      const current = formRef.current;
      if (!mounted.current || ended.current || !current) return;
      setSaveNote('saving');
      try {
        const { status, data } = await post({
          action: 'save',
          token,
          consent: true,
          answers: answersFrom(current.questions, draftRef.current),
        });
        if (!mounted.current || ended.current) return;
        if (status === 200 && data?.ok) {
          setSaveNote('saved');
          return;
        }
        const endView = endViewFor(status, data?.error);
        if (endView) return end(endView);
        if (status === 409 && data?.error === 'already-submitted') return end({ kind: 'submitted' });
        if (status === 400 && data?.error === 'consent-required') {
          // The link was renewed or the answers were cleared: ask for the tick again.
          ended.current = true;
          clearSaveTimer();
          void open();
          return;
        }
        setSaveNote('not-saved');
      } catch {
        if (mounted.current && !ended.current) setSaveNote('not-saved');
      }
    });
  }, [token, end, open]);

  const change = (id: string, value: DraftValue) => {
    const next = { ...draftRef.current, [id]: value };
    draftRef.current = next;
    setDraft(next);
    clearSaveTimer();
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      queueSave();
    }, AUTOSAVE_DELAY_MS);
  };

  const start = async () => {
    if (!form || !consent || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { status, data } = await post({ action: 'start', token, consent: true, version: form.version });
      if (!mounted.current) return;
      if (status === 200 && data?.ok) {
        setIndex(0);
        setView({ kind: 'questions' });
        return;
      }
      if (status === 409 && data?.error === 'questions-changed') {
        await open();
        return;
      }
      const endView = endViewFor(status, data?.error);
      if (endView) return end(endView);
      setError(status === 429 ? RATE_LIMITED_TEXT : ERROR_TEXT);
    } catch {
      if (mounted.current) setError(ERROR_TEXT);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const submit = async () => {
    if (!form || busy) return;
    clearSaveTimer();
    setBusy(true);
    setError(null);
    try {
      // Let any save already on its way land first, so it can never arrive after the submit.
      await saveChain.current;
      const { status, data } = await post({
        action: 'submit',
        token,
        consent: true,
        answers: answersFrom(form.questions, draftRef.current),
      });
      if (!mounted.current) return;
      if (status === 200 && data?.ok && data.state === 'submitted') {
        ended.current = true;
        navigate('/s/done', { replace: true });
        return;
      }
      const endView = endViewFor(status, data?.error);
      if (endView) return end(endView);
      if (status === 400 && data?.error === 'consent-required') {
        await open();
        return;
      }
      if (status === 400) setError(ANSWERS_ERROR_TEXT);
      else setError(status === 429 ? RATE_LIMITED_TEXT : ERROR_TEXT);
    } catch {
      if (mounted.current) setError(ERROR_TEXT);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  switch (view.kind) {
    case 'loading':
      return <Message text="Loading..." />;
    case 'submitted':
      return <ScreeningDone />;
    case 'expired':
      return <Message text={EXPIRED_TEXT} />;
    case 'closed':
      return <Message text={CLOSED_TEXT} />;
    case 'not-found':
      return <Message text={NOT_FOUND_TEXT} />;
    case 'error':
      return <Message text={ERROR_TEXT} />;
    default:
      break;
  }
  if (!form) return <Message text={ERROR_TEXT} />;

  if (view.kind === 'consent') {
    return (
      <div className="form-container">
        <div className="form-header">
          <h1>{form.title || 'A few quick questions'}</h1>
        </div>
        <div className="form-body">
          {error && <div className="error-message">{error}</div>}
          {form.intro && <p className="screening-intro">{form.intro}</p>}
          <p className="screening-payment">{form.paymentWarning}</p>
          {form.expiresOn && <p className="hint">This link works until {form.expiresOn}.</p>}
          <PrivacyNoticeLink href={SCREENING_PRIVACY_PATH} label={SCREENING_PRIVACY_LABEL} />
          <div className="form-group screening-consent">
            <label htmlFor="screening-consent">
              <input
                type="checkbox"
                id="screening-consent"
                checked={consent}
                onChange={e => setConsent(e.target.checked)}
              />{' '}
              {form.consentText}
            </label>
          </div>
          <button type="button" className="submit-btn" disabled={!consent || busy} onClick={() => void start()}>
            Start
          </button>
        </div>
      </div>
    );
  }

  const questions = form.questions;
  const total = questions.length;
  const at = Math.min(index, Math.max(total - 1, 0));
  const q = total > 0 ? questions[at] : null;
  const isLast = total === 0 || at === total - 1;
  const answered = (x: ServedQuestion) => answerFor(x, draft[x.id]) !== undefined;
  const canNext = !!q && (q.optional || answered(q));
  const allAnswered = questions.every(x => x.optional || answered(x));

  return (
    <div className="form-container">
      <div className="form-header">
        <h1>{form.title || 'A few quick questions'}</h1>
      </div>
      <div className="form-body">
        {error && <div className="error-message">{error}</div>}
        {q && (
          <div className="form-group screening-question" key={q.id}>
            <p className="screening-progress">
              Question {at + 1} of {total}
            </p>
            <QuestionInput q={q} value={draft[q.id]} onChange={v => change(q.id, v)} />
            {q.optional && <span className="hint">Optional</span>}
          </div>
        )}
        <p className="hint screening-saved" aria-live="polite">
          {saveNote === 'saved' ? 'Saved' : saveNote === 'not-saved' ? 'Not saved yet' : ''}
        </p>
        <div className="screening-nav">
          {at > 0 && (
            <button type="button" className="screening-nav-btn" onClick={() => setIndex(at - 1)} disabled={busy}>
              Back
            </button>
          )}
          {!isLast && (
            <button type="button" className="screening-nav-btn" onClick={() => setIndex(at + 1)} disabled={!canNext}>
              Next
            </button>
          )}
        </div>
        {isLast && (
          <button type="button" className="submit-btn" disabled={!allAnswered || busy} onClick={() => void submit()}>
            {busy ? 'Sending...' : 'Submit'}
          </button>
        )}
      </div>
    </div>
  );
}

function QuestionInput({ q, value, onChange }: { q: ServedQuestion; value: DraftValue | undefined; onChange: (v: DraftValue) => void }) {
  const inputId = `q-${q.id}`;
  switch (q.type) {
    case 'yesno': {
      const labelId = `${inputId}-label`;
      return (
        <div role="group" aria-labelledby={labelId}>
          <p id={labelId} className="screening-question-text">{q.text}</p>
          <div className="yesno-buttons">
            {[true, false].map(choice => (
              <button
                key={String(choice)}
                type="button"
                className={`yesno-btn${value === choice ? ' selected' : ''}`}
                aria-pressed={value === choice}
                onClick={() => onChange(choice)}
              >
                {choice ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        </div>
      );
    }
    case 'number':
      return (
        <>
          <label htmlFor={inputId} className="screening-question-text">{q.text}</label>
          <input
            id={inputId}
            type="number"
            inputMode="decimal"
            step="any"
            min={q.inputMin}
            max={q.inputMax}
            value={typeof value === 'string' ? value : ''}
            onChange={e => onChange(e.target.value)}
          />
        </>
      );
    case 'choice': {
      const options = q.options ?? [];
      const picked = q.multi ? (Array.isArray(value) ? value : []) : [];
      return (
        <fieldset className="screening-choice">
          <legend className="screening-question-text">{q.text}</legend>
          {q.multi && <span className="hint">Tick one or more</span>}
          <div className="response-options">
            {options.map(o => {
              const checked = q.multi ? picked.includes(o.id) : value === o.id;
              return (
                <label key={o.id} className={`response-option${checked ? ' selected' : ''}`}>
                  <input
                    type={q.multi ? 'checkbox' : 'radio'}
                    name={inputId}
                    value={o.id}
                    checked={checked}
                    onChange={e => {
                      if (!q.multi) return onChange(o.id);
                      onChange(e.target.checked ? [...picked, o.id] : picked.filter(id => id !== o.id));
                    }}
                  />
                  <span className="response-option-label">{o.label}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      );
    }
    case 'text':
      return (
        <>
          <label htmlFor={inputId} className="screening-question-text">{q.text}</label>
          <textarea
            id={inputId}
            maxLength={TEXT_MAX}
            value={typeof value === 'string' ? value : ''}
            onChange={e => onChange(e.target.value)}
          />
        </>
      );
    default:
      return null;
  }
}

export default ScreeningPage;
